import React, { CSSProperties, useEffect, useState } from 'react';
import { PlayerResult } from '@shared/api-dto';
import { getTeamName } from '../shared/room-data-helpers';

import './team-picker.css';

// How long the players sit in a neutral pile before being sorted onto teams.
const SPLIT_DELAY = 1300;
// How long after the split the headline flips to the "done" state.
const SETTLE_DELAY = 1100;

type TeamPickerProps = {
  players: PlayerResult[];
};

/**
 * Intro marquee for move 1: every player starts in one neutral stack in the
 * middle of the card, then the team panels wipe in from the edges and each
 * chip flies to its team's column. Layout is driven by CSS variables so the
 * chips animate between the two arrangements without leaving gaps.
 */
export const TeamPicker = ({ players }: TeamPickerProps) => {
  const [split, setSplit] = useState(false);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    const splitTimer = setTimeout(() => setSplit(true), SPLIT_DELAY);
    const settleTimer = setTimeout(() => setSettled(true), SPLIT_DELAY + SETTLE_DELAY);
    return () => {
      clearTimeout(splitTimer);
      clearTimeout(settleTimer);
    };
  }, []);

  const team1 = players.filter(p => p.team === 1);
  const team2 = players.filter(p => p.team !== 1);
  const rows = split ? Math.max(team1.length, team2.length) : players.length;

  const className = [
    'teamPicker',
    split ? 'split' : '',
    settled ? 'settled' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={className} style={{ '--rows': rows } as CSSProperties}>
      <div className='teamPanel team1' />
      <div className='teamPanel team2' />

      <div className='pickHeader'>
        <p className='pickTitle chevrons'>
          {settled
            ? <span>Teams are set!</span>
            : (
              <span>
                Picking teams
                <span className='marqueeDots'>
                  <span>.</span><span>.</span><span>.</span>
                </span>
              </span>
              )}
        </p>
      </div>

      <div className='pickStage'>
        <h2 className='pickTeamTitle team1'>{`Team ${getTeamName(1)}`}</h2>
        <h2 className='pickTeamTitle team2'>{`Team ${getTeamName(2)}`}</h2>
        {players.map((p, i) => {
          const onTeam1 = p.team === 1;
          const teamIndex = (onTeam1 ? team1 : team2).indexOf(p);
          const style = {
            '--i': split ? teamIndex : i,
            '--order': i,
          } as CSSProperties;
          return (
            <div key={p.user.guid} className={`pickChip ${onTeam1 ? 'team1' : 'team2'}`} style={style}>
              <div className='pickChipCard'>
                <span className={`avatar ${split ? (onTeam1 ? 'team1' : 'team2') : ''}`}>
                  {p.user.name.trim().charAt(0)}
                </span>
                <span className='pickChipName'>{p.user.name}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
